import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { generateEditUrl } from '../../core/utils/urlUtils';
import { useUserPreferences } from '../../core/context/UserPreferencesContext';
import { getDisplayDate } from '../../core/utils/dateUtils';
import { 
  LogoIcon,
  SearchIcon,
  EditIcon,
  TrashIcon,
  AnalyticsIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  PublishIcon,
  DraftIcon,
  CheckIcon,
  XIcon,
  PencilIcon,
  CopyIcon,
  ExternalLinkIcon,
  SortOrderIcon,
  SortAlphaIcon,
  GlobeIcon,
  SettingsDotsIcon,
  ConfirmDialog,
  EmptyState,
  StatusBadge,
} from "../../design-system";
import { SearchInput } from "../../design-system/components/inputs";
import { apiFetch } from "../../core/utils/api";
import { APP_DOMAIN, APP_URL_WWW } from "../../core/utils/config";
import { typography, surfaces, borders, textColors, brandColors, interactive, effects } from "../../design-system/tokens";
import { VersionIcon } from "../navbar/icons";
import WebsiteSettingsModal from './WebsiteSettingsModal';
import SubscriptionPlansModal from "../../subscription/SubscriptionPlansModal";

// Confetti Component
const Confetti = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => {
        onComplete();
      }, 3000); // Celebration lasts 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);

  if (!isActive) return null;

  // Create confetti pieces
  const confettiPieces = Array.from({ length: 50 }, (_, i) => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'];
    const shapes = ['circle', 'square', 'triangle'];
    
    return {
      id: i,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      left: Math.random() * 100,
      animationDelay: Math.random() * 2,
      animationDuration: 2 + Math.random() * 2,
      size: 4 + Math.random() * 8,
    };
  });

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
             {/* Celebration Text */}
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.1, 1], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2, times: [0, 0.3, 0.9, 1] }}
      >
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-8 py-4 shadow-2xl border-2 border-green-200">
          <div className="flex justify-center mb-2">
            <div className="w-12 h-12 rounded-full bg-green-400 text-white flex items-center justify-center">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <div className="font-heading text-2xl font-bold text-green-400 text-center mb-1">Published!</div>
          <div className="text-lg text-slate-700 dark:text-dark-200 text-center">
            Your website is now live!
          </div>
        </div>
      </motion.div>

             {/* Confetti Pieces */}
      {confettiPieces.map((piece) => (
        <div
          key={piece.id}
          className={`absolute animate-confetti-fall ${
            piece.shape === 'circle' ? 'rounded-full' : 
            piece.shape === 'triangle' ? 'confetti-triangle' : ''
          }`}
          style={{
            left: `${piece.left}%`,
            top: `-300px`,
            width: `${piece.size}px`,
            height: `${piece.size}px`,
            backgroundColor: piece.shape !== 'triangle' ? piece.color : 'transparent',
            borderColor: piece.shape === 'triangle' ? piece.color : 'transparent',
            animationDelay: `${piece.animationDelay}s`,
            animationDuration: `${piece.animationDuration}s`,
            '--random-x': `${(Math.random() - 0.5) * 200}px`,
          }}
        />
      ))}

      <style jsx>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-200px) translateX(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px)) translateX(var(--random-x)) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-confetti-fall {
          animation: confetti-fall linear infinite;
        }
        
        .confetti-triangle {
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 12px solid;
        }
      `}</style>
    </div>
  );
};

const UserWebsites = ({ username, searchTerm, setSearchTerm }) => {
  const [containers, setContainers] = useState([]);
  const [versionsByContainer, setVersionsByContainer] = useState({}); // { [containerId]: { versions: [], loaded: false, loading: false } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAnalyticsMessage, setShowAnalyticsMessage] = useState(null);
  const [expandedContainers, setExpandedContainers] = useState(new Set());
  const [showAllVersions, setShowAllVersions] = useState(new Set()); // Track which containers show all versions
  const [editingVersion, setEditingVersion] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchCloseTimeoutRef = useRef(null);
  const [recentlyPublished, setRecentlyPublished] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [preparingForEdit, setPreparingForEdit] = useState(null); // Track which website is being prepared
  const [copyingWebsite, setCopyingWebsite] = useState(null); // Track which website is being copied
  const [confirmState, setConfirmState] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [statusFilter, setStatusFilter] = useState("all"); // all | published | draft
  const [sortOrder, setSortOrder] = useState("desc"); // desc = most recent first, asc = least recent first
  const [sortKey, setSortKey] = useState("updated"); // updated | alpha
  // Settings modal state
  const [settingsContainer, setSettingsContainer] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null); // Track which action is being hovered (format: "websiteId-action")
  const searchInputRef = useRef(null);
  const { preferences } = useUserPreferences();
  const navigate = useNavigate();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [publishLimitInfo, setPublishLimitInfo] = useState(null);
  const [createLimitInfo, setCreateLimitInfo] = useState(null);
  const [upgradeModalContext, setUpgradeModalContext] = useState(null); // "publish" | "create" | null
  const [editingContainer, setEditingContainer] = useState(null); // containerId being prepared for edit at container level

  const fetchContainers = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await apiFetch("/api/containers/", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setContainers(data);
      } else if (!silent) {
        setError("Failed to fetch containers");
      }
    } catch (err) {
      if (!silent) setError("Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchContainers();
  }, [fetchContainers]);

  // Poll for updates while any container has an active AI generation
  const AI_POLL_INTERVAL_MS = 8_000;
  useEffect(() => {
    const hasActiveGeneration = containers.some((c) => c.has_active_ai_generation);
    if (!hasActiveGeneration) return;

    const intervalId = setInterval(() => {
      fetchContainers({ silent: true });
      // Also refresh versions for any expanded containers with active generation
      for (const c of containers) {
        if (c.has_active_ai_generation && expandedContainers.has(c.id)) {
          fetchContainerVersions(c.id);
        }
      }
    }, AI_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [containers, expandedContainers, fetchContainers]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContainerVersions = async (containerId) => {
    setVersionsByContainer((prev) => ({
      ...prev,
      [containerId]: { ...(prev[containerId] || {}), loading: true },
    }));
    try {
      const response = await apiFetch(`/api/containers/${containerId}/versions/`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setVersionsByContainer((prev) => ({
          ...prev,
          [containerId]: { versions: data.versions || [], loaded: true, loading: false },
        }));
      } else {
        setVersionsByContainer((prev) => ({
          ...prev,
          [containerId]: { versions: [], loaded: true, loading: false },
        }));
      }
    } catch (e) {
      setVersionsByContainer((prev) => ({
        ...prev,
        [containerId]: { versions: [], loaded: true, loading: false },
      }));
    }
  };

  // Clear recently published highlight after a delay
  useEffect(() => {
    if (recentlyPublished) {
      const timeout = setTimeout(() => {
        setRecentlyPublished(null);
      }, 2000); // Remove highlight after 2 seconds
      
      return () => clearTimeout(timeout);
    }
  }, [recentlyPublished]);

  // Filter containers based on search term (also check loaded versions by name if available)
  const searchedContainers = containers.filter((container) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = container.name?.toLowerCase().includes(searchLower);
    const domainMatch = container.domain?.toLowerCase().includes(searchLower);
    if (nameMatch || domainMatch) return true;
    const containerVersions = versionsByContainer[container.id]?.versions || [];
    return containerVersions.some((v) => (v.version_name || "Untitled").toLowerCase().includes(searchLower));
  });

  // Determine visibility of filters
  const hasPublished = containers.some((c) => !!c.has_published);
  const hasDraft = containers.some((c) => !c.has_published);
  const showStatusFilter = hasPublished && hasDraft;

  // Apply status filter
  const filteredByStatus = searchedContainers.filter((c) => {
    if (statusFilter === "published") return !!c.has_published;
    if (statusFilter === "draft") return !c.has_published;
    return true; // all
  });

  // Generic sort helper (alpha or updated)
  const sortWithinGroup = (list) => {
    if (sortKey === "alpha") {
      const withNames = list.map((c) => ({ c, n: (c.name || "").toLowerCase() }));
      withNames.sort((a, b) => {
        if (a.n < b.n) return sortOrder === "asc" ? -1 : 1;
        if (a.n > b.n) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
      return withNames.map(({ c }) => c);
    }
    // updated (fallback to created_at if updated_at missing)
    const withDates = list.map((c) => ({
      c,
      t: new Date(c.updated_at || c.created_at || 0).getTime() || 0,
    }));
    withDates.sort((a, b) => (sortOrder === "desc" ? b.t - a.t : a.t - b.t));
    return withDates.map(({ c }) => c);
  };

  // When viewing "all", do NOT group by published status; just apply the chosen sort
  const finalContainers = sortWithinGroup(filteredByStatus);

  const showSortToggle = finalContainers.length > 1;

  const toggleContainer = (containerId) => {
    const newExpanded = new Set(expandedContainers);
    if (newExpanded.has(containerId)) {
      newExpanded.delete(containerId);
    } else {
      newExpanded.add(containerId);
      // Lazy-load versions if not loaded yet
      if (!versionsByContainer[containerId]?.loaded && !versionsByContainer[containerId]?.loading) {
        fetchContainerVersions(containerId);
      }
    }
    setExpandedContainers(newExpanded);
  };

  const toggleShowAllVersions = (containerId) => {
    const newShowAll = new Set(showAllVersions);
    if (newShowAll.has(containerId)) {
      newShowAll.delete(containerId);
    } else {
      newShowAll.add(containerId);
    }
    setShowAllVersions(newShowAll);
  };

  const handleDeleteWebsite = (websiteId, containerId) => {
    setConfirmState({
      open: true,
      title: "Delete version",
      message: "Are you sure you want to delete this website version?",
      onConfirm: async () => {
        try {
          const response = await apiFetch(`/api/websites/${websiteId}/delete/`, {
            method: "DELETE",
            credentials: "include",
          });
          if (response.ok) {
            // Refresh versions for this container
            await fetchContainerVersions(containerId);
            const updated = versionsByContainer[containerId]?.versions || [];
            if (updated.length === 0) {
              // Remove the empty container from list
              setContainers((prev) => prev.filter((c) => c.id !== containerId));
            }
          } else {
            alert("Failed to delete website");
          }
        } catch (err) {
          alert("Network error while deleting website");
        }
      }
    });
  };

  const handleDeleteContainer = (containerId) => {
    setConfirmState({
      open: true,
      title: "Delete website",
      message: "Are you sure you want to delete this entire website container and all its versions?",
      onConfirm: async () => {
        try {
          const response = await apiFetch(`/api/containers/${containerId}/delete/`, {
            method: "DELETE",
            credentials: "include",
          });
          if (response.ok) {
            setContainers((prev) => prev.filter((c) => c.id !== containerId));
            setVersionsByContainer((prev) => {
              const copy = { ...prev };
              delete copy[containerId];
              return copy;
            });
          } else {
            alert("Failed to delete container");
          }
        } catch (err) {
          alert("Network error while deleting container");
        }
      }
    });
  };

  const handlePublishWebsite = async (websiteId, containerId) => {
    try {
      // Gate publishing by plan only when this publish would increase the number of published websites.
      const container = (containers || []).find((c) => c.id === containerId);
      const containerAlreadyPublished = Boolean(container?.has_published);
      if (!containerAlreadyPublished) {
        try {
          const res = await apiFetch("/api/subscription/limits/", { credentials: "include" });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.limits_reached?.published) {
            setPublishLimitInfo({
              plan_display_name: data?.plan?.display_name || data?.plan?.name || "your plan",
              max_published_websites: data?.plan?.max_published_websites,
              current_published: data?.usage?.current_published,
            });
            setUpgradeModalContext("publish");
            setUpgradeModalOpen(true);
            return;
          }
        } catch (_) {
          // If the gate check fails, proceed and let backend validations handle it.
        }
      }

      const response = await apiFetch(`/api/websites/${websiteId}/publish-version/`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        // Show celebration effect
        setShowCelebration(true);
        
        // Mark this website as recently published for highlighting
        setRecentlyPublished(websiteId);
        
        // Refresh versions of this container
        await fetchContainerVersions(containerId);
        // Update container has_published flag
        setContainers((prev) => prev.map((c) => c.id === containerId ? { ...c, has_published: true } : c));
      } else {
        alert("Failed to publish website");
      }
    } catch (err) {
      alert("Network error while publishing website");
    }
  };

  const handleCreateNewWebsite = async () => {
    setCreateLimitInfo(null);
    try {
      const res = await apiFetch("/api/subscription/limits/", { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.limits_reached?.websites) {
        setCreateLimitInfo({
          plan_display_name: data?.plan?.display_name || data?.plan?.name || "your plan",
          max_websites: data?.plan?.max_websites,
          current_websites: data?.usage?.current_websites,
        });
        // No inline notice/button; open upgrade modal directly.
        setUpgradeModalContext("create");
        setUpgradeModalOpen(true);
        return;
      }
    } catch (_) {
      // If the gate check fails, proceed and let backend validations handle it.
    }

    navigate(`/dashboard/${username}/create`);
  };

  const handleUnpublishWebsite = async (websiteId, containerId) => {
    try {
      const response = await apiFetch(`/api/websites/${websiteId}/unpublish-version/`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        // Refresh versions and recompute has_published
        await fetchContainerVersions(containerId);
        const anyPublished = (versionsByContainer[containerId]?.versions || []).some(v => v.is_published);
        setContainers((prev) => prev.map((c) => c.id === containerId ? { ...c, has_published: anyPublished } : c));
      } else {
        alert("Failed to unpublish website");
      }
    } catch (err) {
      alert("Network error while unpublishing website");
    }
  };

  const handleAnalyticsClick = (containerId) => {
    setShowAnalyticsMessage(containerId);
    // Auto-hide the message after 3 seconds
    setTimeout(() => {
      setShowAnalyticsMessage(null);
    }, 3000);
  };

  const handleStartEditingVersion = (websiteVersion) => {
    setEditingVersion(websiteVersion.id);
    setEditingName(websiteVersion.version_name || "");
  };

  const handleSaveVersionName = async (websiteId, containerId) => {
    try {
      const response = await apiFetch(`/api/websites/${websiteId}/update/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          version_name: editingName.trim() || null,
        }),
      });

      if (response.ok) {
        // Refresh versions for this container so ordering stays correct
        await fetchContainerVersions(containerId);
        setEditingVersion(null);
        setEditingName("");
      } else {
        alert("Failed to update version name");
      }
    } catch (err) {
      alert("Network error while updating version name");
    }
  };

  const handleCancelEditingVersion = () => {
    setEditingVersion(null);
    setEditingName("");
  };

  const handleSearchClick = () => {
    setIsSearchOpen(true);
    // If a close was scheduled (e.g., due to a blur), cancel it.
    if (searchCloseTimeoutRef.current) {
      try {
        window.clearTimeout(searchCloseTimeoutRef.current);
      } catch (_) {}
      searchCloseTimeoutRef.current = null;
    }
    // Focus the input after the state update
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  };

  const handleSearchBlur = () => {
    // Only close if search is empty.
    // Delay the close by a tick so focus can move into the input (or back to it) without
    // immediately collapsing the UI (helps in some browsers/tests where focus ordering differs).
    if (searchCloseTimeoutRef.current) {
      try {
        window.clearTimeout(searchCloseTimeoutRef.current);
      } catch (_) {}
      searchCloseTimeoutRef.current = null;
    }
    if (!searchTerm.trim()) {
      searchCloseTimeoutRef.current = window.setTimeout(() => {
        searchCloseTimeoutRef.current = null;
        const active = document.activeElement;
        // If focus is currently on the search input, keep it open.
        if (searchInputRef.current && active === searchInputRef.current) return;
        setIsSearchOpen(false);
      }, 0);
    }
  };



  const getDisplayDateForWebsite = (websiteVersion) => {
    return getDisplayDate(websiteVersion, preferences.timezone);
  };

  const handlePrepareForEdit = async (websiteVersion, containerId) => {
    setPreparingForEdit(websiteVersion.id);
    try {
      const response = await apiFetch(`/api/websites/${websiteVersion.id}/prepare-for-edit/`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to prepare website for editing");
      }

      const data = await response.json();
      
      // Navigate to the edit URL using the returned parameters
      const editUrl = generateEditUrl(
        data.edit_url_params.username,
        data.edit_url_params.domain,
        data.edit_url_params.version_slug
      );
      
      // If a new version was created, refresh the websites list and show notification
      if (data.new_version_created) {
        console.log(`New version created: ${data.new_version.version_name} (reason: ${data.reason})`);
        
        // Refresh versions for this container
        await fetchContainerVersions(containerId);
      }
      
      navigate(editUrl);
    } catch (error) {
      console.error("Error preparing website for edit:", error);
      alert("Failed to prepare website for editing. Please try again.");
    } finally {
      setPreparingForEdit(null);
    }
  };

  const handleCopyWebsite = async (websiteVersion, containerId) => {
    setCopyingWebsite(websiteVersion.id);
    try {
      const response = await apiFetch(`/api/websites/${websiteVersion.id}/copy/`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to copy website");
      }

      const data = await response.json();
      
      console.log(`Website copied: ${data.new_copy.version_name} created from ${data.original_version.version_name}`);
      
      // Refresh versions for this container to show the new copy
      await fetchContainerVersions(containerId);
    } catch (error) {
      console.error("Error copying website:", error);
      alert("Failed to copy website. Please try again.");
    } finally {
      setCopyingWebsite(null);
    }
  };

  const openContainerSettings = async (container) => {
    // Ensure versions are loaded
    if (!versionsByContainer[container.id]?.loaded && !versionsByContainer[container.id]?.loading) {
      await fetchContainerVersions(container.id);
    }
    setSettingsContainer(container);
  };

  // Edit the latest (most recently updated) version of a container directly from the container row.
  const handleEditLatestVersion = async (containerId) => {
    if (editingContainer === containerId) return; // already in flight
    setEditingContainer(containerId);
    try {
      // Use already-loaded versions if available; otherwise fetch them.
      let versions = versionsByContainer[containerId]?.loaded
        ? versionsByContainer[containerId].versions
        : null;

      if (!versions) {
        const response = await apiFetch(`/api/containers/${containerId}/versions/`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error("Failed to load versions");
        const data = await response.json();
        versions = data.versions || [];
        setVersionsByContainer((prev) => ({
          ...prev,
          [containerId]: { versions, loaded: true, loading: false },
        }));
      }

      if (!versions.length) throw new Error("No versions found");

      // Pick the version with the most recent updated_at (fall back to created_at).
      const latest = [...versions].sort((a, b) => {
        const tA = new Date(a.updated_at || a.created_at || 0).getTime();
        const tB = new Date(b.updated_at || b.created_at || 0).getTime();
        return tB - tA;
      })[0];

      // handlePrepareForEdit navigates away; it manages its own preparingForEdit state.
      await handlePrepareForEdit(latest, containerId);
    } catch (error) {
      console.error("Error editing latest version:", error);
      alert("Failed to open the editor. Please try again.");
    } finally {
      setEditingContainer(null);
    }
  };

  const closeContainerSettings = () => {
    setSettingsContainer(null);
  };

  const handleSettingsSaved = (updatedContainer) => {
    // Update container in the list
    setContainers((prev) => prev.map((c) => 
      c.id === updatedContainer.id ? { ...c, ...updatedContainer } : c
    ));
  };

  const websitesPanelClassName = `${surfaces.glassCard} border ${borders.glass} rounded-3xl p-6 md:p-8 shadow-xl dark:bg-dark-950/60`;
  const quickEditButtonClassName = `inline-flex items-center gap-1.5 px-4 py-2 rounded-xl ${typography.button} ${interactive.primaryGradientBtn} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`;

  if (loading) {
    return (
      <div className={websitesPanelClassName}>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6">Your Websites</h2>
        <div className="flex items-center justify-center h-32">
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400`}></div>
        </div>
      </div>
      
    );
  }

  if (error) {
    return (
      <div className={websitesPanelClassName}>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6">Your Websites</h2>
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <LogoIcon className="w-16 h-16" color="#60a5fa" />
          </div>
          <h3 className="font-heading text-xl font-bold text-blue-600 dark:text-blue-400 mb-2">
            Oops! Something went wrong
          </h3>
          <p className="text-slate-600 dark:text-dark-300 mb-4">
            We're experiencing some technical difficulties loading your websites.
          </p>
          <p className="text-sm text-slate-500 dark:text-dark-400 mb-6">
            Our team is working to fix this issue. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors shadow-sm"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SubscriptionPlansModal
        isOpen={upgradeModalOpen}
        onClose={() => {
          setUpgradeModalOpen(false);
          setUpgradeModalContext(null);
        }}
        notice={
          upgradeModalContext === "create" ? (
            <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
              <div className="font-heading text-lg font-bold text-slate-900 dark:text-white">Upgrade required to create more websites</div>
              <div className="text-sm text-slate-600 dark:text-dark-300 mt-1">
                Your current plan allows up to{" "}
                <span className="text-slate-900 dark:text-white font-medium">{createLimitInfo?.max_websites ?? "0"}</span>{" "}
                website draft{Number(createLimitInfo?.max_websites || 0) === 1 ? "" : "s"}.
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
              <div className="font-heading text-lg font-bold text-slate-900 dark:text-white">Upgrade required to publish</div>
              <div className="text-sm text-slate-600 dark:text-dark-300 mt-1">
                Your current plan allows up to{" "}
                <span className="text-slate-900 dark:text-white font-medium">{publishLimitInfo?.max_published_websites ?? "0"}</span>{" "}
                published website{Number(publishLimitInfo?.max_published_websites || 0) === 1 ? "" : "s"}.
              </div>
            </div>
          )
        }
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.open}
        onClose={() => setConfirmState({ open: false, title: "", message: "", onConfirm: null })}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Celebration Confetti Overlay */}
      <Confetti 
        isActive={showCelebration} 
        onComplete={() => setShowCelebration(false)} 
      />
      
      <div className={websitesPanelClassName}>
        {/* Mobile Layout */}
        <div className="block md:hidden mb-6">
          <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-4 text-center">Your Websites</h2>
          {containers.length > 0 && (
            <>
              {!isSearchOpen ? (
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleSearchClick}
                    className={interactive.glassIconBtn}
                    title="Search websites"
                  >
                    <SearchIcon size="md" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateNewWebsite}
                    className={`px-4 py-2 rounded-xl font-medium ${interactive.primaryGradientBtn}`}
                  >
                    + Create New
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <SearchInput
                    ref={searchInputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onBlur={handleSearchBlur}
                    onClear={() => {
                      setSearchTerm("");
                      setIsSearchOpen(false);
                    }}
                    placeholder="Search websites..."
                    autoFocus
                    fullWidth
                  />
                  <button
                    type="button"
                    onClick={handleCreateNewWebsite}
                    className={`px-5 py-2.5 rounded-xl font-medium ${interactive.primaryGradientBtn}`}
                  >
                    + Create New
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Your Websites</h2>
          {containers.length > 0 && (
          <div className="flex items-center space-x-3">
            {/* Search Section */}
            <div className="flex items-center space-x-2">
              {!isSearchOpen ? (
                <button
                  onClick={handleSearchClick}
                  className={interactive.glassIconBtn}
                  title="Search websites"
                >
                  <SearchIcon />
                </button>
              ) : (
                <div className="w-64">
                  <SearchInput
                    ref={searchInputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onBlur={handleSearchBlur}
                    onClear={() => {
                      setSearchTerm("");
                      setIsSearchOpen(false);
                    }}
                    placeholder="Search websites..."
                    autoFocus
                  />
                </div>
              )}
            </div>
            {showStatusFilter && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 dark:border-dark-700 rounded-xl text-sm font-medium text-slate-700 dark:text-dark-300 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm hover:border-slate-300 dark:hover:border-dark-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                title="Filter by status"
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            )}
            {showSortToggle && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (sortKey === 'updated') {
                      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                    } else {
                      setSortKey('updated');
                    }
                  }}
                  className={`p-2 border rounded-xl transition-all ${
                    sortKey === 'updated' 
                      ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" 
                      : "border-slate-200 dark:border-dark-700 bg-white/50 dark:bg-dark-800/50 text-slate-500 dark:text-dark-400 hover:bg-white/80 dark:hover:bg-dark-700/80"
                  }`}
                  title={sortKey === 'updated' ? `Sort by ${sortOrder === 'desc' ? 'oldest' : 'newest'} first` : 'Sort by last update'}
                >
                  <SortOrderIcon order={sortOrder} active={sortKey === 'updated'} />
                </button>
                <button
                  onClick={() => {
                    if (sortKey === 'alpha') {
                      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                    } else {
                      setSortKey('alpha');
                    }
                  }}
                  className={`p-2 border rounded-xl transition-all ${
                    sortKey === 'alpha' 
                      ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" 
                      : "border-slate-200 dark:border-dark-700 bg-white/50 dark:bg-dark-800/50 text-slate-500 dark:text-dark-400 hover:bg-white/80 dark:hover:bg-dark-700/80"
                  }`}
                  title={sortKey === 'alpha' ? `Sort ${sortOrder === 'desc' ? 'Z to A' : 'A to Z'}` : 'Sort alphabetically'}
                >
                  <SortAlphaIcon order={sortOrder} active={sortKey === 'alpha'} />
                </button>

              </div>
            )}

            <button
              type="button"
              onClick={handleCreateNewWebsite}
              className={`px-5 py-2.5 rounded-xl font-medium ${interactive.primaryGradientBtn}`}
            >
              + Create New Website
            </button>
          </div>
          )}
        </div>

        {/* Search Results Summary */}
        {searchTerm.trim() && (
          <div className="mb-4 text-sm text-slate-500 dark:text-dark-400">
            {finalContainers.length === 0
              ? `No websites found for "${searchTerm}"`
              : `Showing ${finalContainers.length} container${finalContainers.length !== 1 ? 's' : ''} for '${searchTerm}"`}
          </div>
        )}

        {finalContainers.length === 0 ? (
          searchTerm.trim() ? (
            // Search results empty state
            <EmptyState
              icon={<SearchIcon size="3xl" color="neutral" />}
              title="No websites found"
              description="Try searching with different keywords"
              action={
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setIsSearchOpen(false);
                  }}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                >
                  Clear search
                </button>
              }
            />
          ) : (
            // No websites at all empty state
            <EmptyState
              icon={<GlobeIcon size="3xl" color="primary" />}
              title="No websites yet"
              description="Create your first website to get started"
              action={
                <motion.button
                  type="button"
                  onClick={handleCreateNewWebsite}
                  className={`px-8 py-4 rounded-2xl font-bold text-lg ${interactive.primaryGradientBtn}`}
                  animate={{
                    boxShadow: [
                      "0 0 0 2px rgba(96,165,250,0.34), 0 0 26px 10px rgba(59,130,246,0.34), 0 0 84px 26px rgba(37,99,235,0.26), 0 25px 50px -12px rgba(0,0,0,0.25)",
                      "0 0 0 3px rgba(147,197,253,0.48), 0 0 34px 12px rgba(59,130,246,0.42), 0 0 104px 34px rgba(37,99,235,0.32), 0 25px 50px -12px rgba(0,0,0,0.25)",
                      "0 0 0 2px rgba(96,165,250,0.34), 0 0 26px 10px rgba(59,130,246,0.34), 0 0 84px 26px rgba(37,99,235,0.26), 0 25px 50px -12px rgba(0,0,0,0.25)",
                    ],
                  }}
                  transition={{
                    duration: 2.4,
                    ease: "easeInOut",
                    repeat: Infinity,
                  }}
                >
                  Create Your First Website
                </motion.button>
              }
            />
          )
        ) : (
          <div className="space-y-4">
            {finalContainers.map((container) => {
              const containerId = container.id;
              const isExpanded = expandedContainers.has(containerId);
              const showingAllVersions = showAllVersions.has(containerId);
              const allVersions = versionsByContainer[containerId]?.versions || [];
              const displayedVersions = showingAllVersions ? allVersions : allVersions.slice(0, 3);
              const hasMoreVersions = allVersions.length > 3;
              const isPublishedContainer = !!container.has_published;
              const isAiGenerating = !!container.has_active_ai_generation;
              // The "latest" version is the one most recently edited (updated_at, fall back to created_at).
              const latestVersionId = allVersions.length
                ? [...allVersions].sort((a, b) => {
                    const tA = new Date(a.updated_at || a.created_at || 0).getTime();
                    const tB = new Date(b.updated_at || b.created_at || 0).getTime();
                    return tB - tA;
                  })[0].id
                : null;

              return (
                <motion.div 
                  key={containerId} 
                  className={`border ${borders.glass} bg-white/40 dark:bg-dark-800/40 backdrop-blur-sm rounded-2xl overflow-visible shadow-sm`}
                  layout
                  transition={{ duration: 0.3 }}
                >
                  {/* Container Header - Clickable */}
                  <div 
                    className={`p-4 md:p-5 rounded-t-2xl ${isExpanded ? '' : 'rounded-b-2xl'} hover:bg-white/60 dark:hover:bg-dark-700/60 cursor-pointer transition-colors`}
                    onClick={() => toggleContainer(containerId)}
                  >
                    {/* Desktop Layout */}
                    <div className="hidden md:flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {/* Expand/Collapse Icon */}
                        <div className="text-slate-400 dark:text-dark-500">
                          {isExpanded ? <ChevronDownIcon size="md" /> : <ChevronRightIcon size="md" />}
                        </div>
                        
                        {/* Container Icon */}
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-sky-400 rounded-xl flex items-center justify-center shadow-inner">
                          <span className="text-white font-bold text-lg">
                            {container.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Container Info */}
                        <div>
                          <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">
                            {container.name}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-dark-400 mt-1">
                            {container.domain ? (
                              isPublishedContainer ? (
                                <a
                                  href={`${APP_URL_WWW}/${username}/${container.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 dark:text-blue-400 hover:underline flex items-center group font-medium"
                                >
                                  {`${APP_DOMAIN}/${username}/${container.domain}`}
                                  <ExternalLinkIcon inline />
                                </a>
                              ) : (
                                <span>{`${APP_DOMAIN}/${username}/${container.domain}`}</span>
                              )
                            ) : (
                              <span className="italic">No domain set</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Status Badge and Container Actions */}
                      <div className="flex items-center space-x-4">
                        {/* Status Badge */}
                        <StatusBadge variant={isPublishedContainer ? "success" : "warning"}>
                          {isPublishedContainer ? "Published" : "Unpublished"}
                        </StatusBadge>

                        {/* Quick Edit Button (latest version) / AI Building indicator */}
                        {isAiGenerating ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 cursor-default select-none"
                            title="AI is building this website…"
                            aria-label="AI is building this website"
                          >
                            <div className="w-3.5 h-3.5 animate-spin border-2 border-amber-500 dark:border-amber-400 border-t-transparent rounded-full" aria-hidden="true" />
                            <span>AI Building…</span>
                          </span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditLatestVersion(containerId);
                            }}
                            disabled={editingContainer === containerId}
                            className={quickEditButtonClassName}
                            title={editingContainer === containerId ? "Opening editor…" : "Edit latest version"}
                            aria-label="Edit website"
                          >
                            {editingContainer === containerId ? (
                              <>
                                <div className="w-3.5 h-3.5 animate-spin border-2 border-current border-t-transparent rounded-full" aria-hidden="true" />
                                <span>Opening…</span>
                              </>
                            ) : (
                              <>
                                <EditIcon size="sm" />
                                <span>Edit</span>
                              </>
                            )}
                          </button>
                        )}
                        
                        {/* Container Actions */}
                          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                            <div className="relative">
                              <button
                                className={interactive.glassIconBtn}
                                title="Analytics"
                                onClick={() => handleAnalyticsClick(containerId)}
                              >
                              <AnalyticsIcon />
                            </button>
                            {showAnalyticsMessage === containerId && (
                              <div className="absolute top-full mt-2 right-0 bg-gray-800 dark:bg-dark-700 text-white text-xs px-3 py-2 rounded-md shadow-lg z-50 whitespace-nowrap">
                                Coming soon!
                                <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-800 dark:bg-dark-700 rotate-45"></div>
                              </div>
                            )}
                          </div>

                            {/* Versions toggle button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleContainer(containerId);
                              }}
                              className={`p-2 rounded-xl transition-all ${
                                isExpanded
                                  ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                                  : "text-slate-500 hover:bg-white/50 dark:hover:bg-dark-800/50 hover:text-slate-900 dark:hover:text-white"
                              }`}
                              title={isExpanded ? "Hide versions" : "Show versions"}
                            aria-label={isExpanded ? "Hide versions" : "Show versions"}
                            aria-pressed={isExpanded}
                          >
                            <VersionIcon />
                          </button>

                            <button
                              onClick={() => openContainerSettings(container)}
                              className={interactive.glassIconBtn}
                              title="Website settings"
                              aria-label="Website settings"
                            >
                            <SettingsDotsIcon size="md" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="block md:hidden space-y-3">
                      {/* Top Row: Icon and Info */}
                      <div className="flex items-start space-x-3">
                        {/* Expand/Collapse Icon */}
                        <div className="text-slate-400 dark:text-dark-500 mt-1">
                          {isExpanded ? <ChevronDownIcon size="md" /> : <ChevronRightIcon size="md" />}
                        </div>
                        
                        {/* Container Icon */}
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-sky-400 rounded-xl flex items-center justify-center flex-shrink-0 shadow-inner">
                          <span className="text-white font-bold text-lg">
                            {container.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Container Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">
                            {container.name}
                          </h3>
                          <div className="text-sm text-slate-500 dark:text-dark-400 mt-0.5 break-all">
                            {container.domain ? (
                              isPublishedContainer ? (
                                <a
                                  href={`${APP_URL_WWW}/${username}/${container.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center group font-medium"
                                >
                                  {`${APP_DOMAIN}/${username}/${container.domain}`}
                                  <ExternalLinkIcon inline />
                                </a>
                              ) : (
                                <span>{`${APP_DOMAIN}/${username}/${container.domain}`}</span>
                              )
                            ) : (
                              <span className="italic">No domain set</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bottom Rows: Status Badge on first line, actions on second */}
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        {/* Status Badge Row - centered */}
                        <div className="flex justify-center">
                          <StatusBadge
                            variant={isPublishedContainer ? "success" : "warning"}
                          >
                            {isPublishedContainer ? "Published" : "Unpublished"}
                          </StatusBadge>
                        </div>

                        {/* Actions Row: Edit left-aligned, icon buttons right-aligned */}
                        <div className="flex items-center justify-between">
                          {/* Quick Edit Button (latest version) / AI Building indicator - left */}
                          {isAiGenerating ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 cursor-default select-none"
                              title="AI is building this website…"
                              aria-label="AI is building this website"
                            >
                              <div className="w-3.5 h-3.5 animate-spin border-2 border-amber-500 dark:border-amber-400 border-t-transparent rounded-full" aria-hidden="true" />
                              <span>AI Building…</span>
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditLatestVersion(containerId);
                              }}
                              disabled={editingContainer === containerId}
                              className={quickEditButtonClassName}
                              title={editingContainer === containerId ? "Opening editor…" : "Edit latest version"}
                              aria-label="Edit website"
                            >
                              {editingContainer === containerId ? (
                                <div className="w-3.5 h-3.5 animate-spin border-2 border-current border-t-transparent rounded-full" aria-hidden="true" />
                              ) : (
                                <EditIcon size="sm" />
                              )}
                              <span>{editingContainer === containerId ? "Opening…" : "Edit"}</span>
                            </button>
                          )}

                          {/* Icon buttons - right */}
                          <div className="flex items-center gap-1">
                            <div className="relative">
                              <button
                                className={interactive.glassIconBtn}
                                title="Analytics"
                                onClick={() => handleAnalyticsClick(containerId)}
                              >
                                <AnalyticsIcon />
                              </button>
                              {showAnalyticsMessage === containerId && (
                                <div className="absolute top-full mt-2 right-0 bg-gray-800 dark:bg-dark-700 text-white text-xs px-3 py-2 rounded-md shadow-lg z-50 whitespace-nowrap">
                                  Coming soon!
                                  <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-800 dark:bg-dark-700 rotate-45"></div>
                                </div>
                              )}
                            </div>

                            {/* Versions toggle button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleContainer(containerId);
                              }}
                              className={`p-2 rounded-xl transition-all ${
                                isExpanded
                                  ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                                  : "text-slate-500 hover:bg-white/50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-white"
                              }`}
                              title={isExpanded ? "Hide versions" : "Show versions"}
                              aria-label={isExpanded ? "Hide versions" : "Show versions"}
                              aria-pressed={isExpanded}
                            >
                              <VersionIcon />
                            </button>

                            <button
                              onClick={() => openContainerSettings(container)}
                              className={interactive.glassIconBtn}
                              title="Website settings"
                              aria-label="Website settings"
                            >
                              <SettingsDotsIcon size="md" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Website Versions */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div 
                        className={`border-t ${borders.glass} bg-white/20 dark:bg-dark-900/20 rounded-b-2xl p-4 md:p-5`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="space-y-2">
                          {/* Versions section heading */}
                          <div className={`pb-2 mb-1 border-b ${borders.glass} flex items-center justify-between`}>
                            <h4 className="font-heading text-xs font-bold text-slate-500 dark:text-dark-400 uppercase tracking-wider">
                              Versions
                            </h4>
                            <span className="text-xs font-medium text-slate-500 dark:text-dark-400">
                              {allVersions.length} version{allVersions.length !== 1 ? "s" : ""}
                            </span>
                          </div>

                          <AnimatePresence>
                            {displayedVersions.map((website) => (
                              <motion.div
                                key={website.id}
                                layoutId={`website-${website.id}`}
                                className={`p-3 md:p-4 rounded-xl transition-all duration-300 ${
                                  recentlyPublished === website.id
                                    ? "bg-green-50/80 dark:bg-green-900/30 border border-green-200 dark:border-green-800 shadow-md"
                                    : "bg-white/50 dark:bg-dark-800/50 hover:bg-white/80 dark:hover:bg-dark-700/80 border border-transparent hover:border-slate-200 dark:hover:border-dark-700"
                                }`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                layout
                                transition={{ 
                                  duration: 0.4,
                                  type: "spring",
                                  bounce: 0.3
                                }}
                              >
                                {/* Desktop Layout: Single row with all content */}
                                <div className="hidden md:flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    {/* Status Badge */}
                                    <motion.div
                                      className="inline-flex"
                                      animate={recentlyPublished === website.id ? {
                                        scale: [1, 1.1, 1],
                                      } : {}}
                                      transition={{ duration: 0.4 }}
                                    >
                                      <StatusBadge variant={website.is_published ? "success" : "warning"} size="sm">
                                        {website.is_published ? "Published" : "Draft"}
                                      </StatusBadge>
                                    </motion.div>

                                    {/* Latest badge */}
                                    {website.id === latestVersionId && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                        Latest
                                      </span>
                                    )}
                                    
                                    {/* Website Info */}
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        {editingVersion === website.id ? (
                                          <>
                                            <input
                                              type="text"
                                              value={editingName}
                                              onChange={(e) => setEditingName(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  handleSaveVersionName(website.id, containerId);
                                                } else if (e.key === 'Escape') {
                                                  handleCancelEditingVersion();
                                                }
                                              }}
                                              className="text-sm font-medium text-slate-900 dark:text-white border border-slate-300 dark:border-dark-600 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm"
                                              placeholder="Untitled"
                                              autoFocus
                                            />
                                            <button
                                              onClick={() => handleSaveVersionName(website.id, containerId)}
                                              className="p-1 text-green-600 hover:text-green-800 transition-colors"
                                              title="Save"
                                            >
                                              <CheckIcon size="sm" />
                                            </button>
                                            <button
                                              onClick={handleCancelEditingVersion}
                                              className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                              title="Cancel"
                                            >
                                              <XIcon size="sm" />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                              {website.version_name || "Untitled"}
                                            </p>
                                            <button
                                              onClick={() => handleStartEditingVersion(website)}
                                              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                              title="Edit version name"
                                            >
                                              <PencilIcon size="sm" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      <p className="text-xs text-slate-500 dark:text-dark-400 mt-0.5">
                                        Last edited: {getDisplayDateForWebsite(website)}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Version Actions */}
                                  <div className="flex items-center space-x-2">
                                    {/* Action Label - Shows on left when hovering */}
                                    <AnimatePresence>
                                      {hoveredAction?.startsWith(`${website.id}-`) && (
                                        <motion.div
                                          initial={{ opacity: 0, x: 10 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          exit={{ opacity: 0, x: 10 }}
                                          transition={{ duration: 0.2 }}
                                          className="text-sm text-slate-600 dark:text-dark-300 font-medium mr-2 whitespace-nowrap"
                                        >
                                          {hoveredAction === `${website.id}-visit` && "Visit website"}
                                          {hoveredAction === `${website.id}-publish` && "Publish this version"}
                                          {hoveredAction === `${website.id}-unpublish` && "Unpublish this version"}
                                          {hoveredAction === `${website.id}-edit` && (website.ai_generation_status === "queued" || website.ai_generation_status === "running" ? "AI is building…" : "Edit this version")}
                                          {hoveredAction === `${website.id}-copy` && "Copy this version"}
                                          {hoveredAction === `${website.id}-delete` && "Delete this version"}
                                        </motion.div>
                                      )}
                                    </AnimatePresence>

                                    {/* Go To Published Website */}
                                    {website.is_published && website.domain && (
                                      <a
                                        href={`${APP_URL_WWW}/${username}/${website.domain}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 text-slate-500 hover:bg-indigo-500 hover:text-white rounded-xl transition-all group"
                                        title="Open Published Website"
                                        onMouseEnter={() => setHoveredAction(`${website.id}-visit`)}
                                        onMouseLeave={() => setHoveredAction(null)}
                                      >
                                        <ExternalLinkIcon size="md" />
                                      </a>
                                    )}

                                    {/* Publish/Unpublish Button */}
                                    {website.is_published ? (
                                      <button
                                        onClick={() => handleUnpublishWebsite(website.id, containerId)}
                                        className="p-2 text-slate-500 hover:bg-yellow-500 hover:text-white rounded-xl transition-all"
                                        title="Unpublish this version"
                                        onMouseEnter={() => setHoveredAction(`${website.id}-unpublish`)}
                                        onMouseLeave={() => setHoveredAction(null)}
                                      >
                                        <DraftIcon size="md" />
                                      </button>
                                    ) : (
                                      <motion.button
                                        onClick={() => handlePublishWebsite(website.id, containerId)}
                                        className="p-2 text-slate-500 hover:bg-green-500 hover:text-white rounded-xl transition-all"
                                        title="Publish this version"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onMouseEnter={() => setHoveredAction(`${website.id}-publish`)}
                                        onMouseLeave={() => setHoveredAction(null)}
                                      >
                                        <PublishIcon size="md" />
                                      </motion.button>
                                    )}
                                    
                                    {/* Edit Button / AI Building indicator */}
                                    {website.ai_generation_status === "queued" || website.ai_generation_status === "running" ? (
                                      <span
                                        className="p-2 rounded-xl text-amber-500 dark:text-amber-400 cursor-default"
                                        title="AI is building this version…"
                                        onMouseEnter={() => setHoveredAction(`${website.id}-edit`)}
                                        onMouseLeave={() => setHoveredAction(null)}
                                      >
                                        <div className="w-5 h-5 animate-spin border-2 border-amber-500 dark:border-amber-400 border-t-transparent rounded-full" />
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handlePrepareForEdit(website, containerId)}
                                        disabled={preparingForEdit === website.id}
                                        className={`p-2 rounded-xl transition-all ${
                                          preparingForEdit === website.id
                                            ? "text-slate-400 cursor-not-allowed"
                                            : "text-slate-500 hover:bg-blue-500 hover:text-white"
                                        }`}
                                        title={preparingForEdit === website.id ? "Preparing…" : "Edit this version"}
                                        onMouseEnter={() => !preparingForEdit && setHoveredAction(`${website.id}-edit`)}
                                        onMouseLeave={() => setHoveredAction(null)}
                                      >
                                        {preparingForEdit === website.id ? (
                                          <div className="w-5 h-5 animate-spin border-2 text-slate-400 border-t-transparent rounded-full"></div>
                                        ) : (
                                          <EditIcon size="md" />
                                        )}
                                      </button>
                                    )}
                                    
                                    {/* Copy Button */}
                                    <button
                                      onClick={() => handleCopyWebsite(website, containerId)}
                                      disabled={copyingWebsite === website.id}
                                      className={`p-2 rounded-xl transition-all ${
                                        copyingWebsite === website.id
                                          ? "text-slate-400 cursor-not-allowed"
                                          : "text-slate-500 hover:bg-purple-500 hover:text-white"
                                      }`}
                                      title={copyingWebsite === website.id ? "Copying…" : "Copy this version"}
                                      onMouseEnter={() => !copyingWebsite && setHoveredAction(`${website.id}-copy`)}
                                      onMouseLeave={() => setHoveredAction(null)}
                                    >
                                      {copyingWebsite === website.id ? (
                                        <div className="w-5 h-5 animate-spin border-2 text-slate-400 border-t-transparent rounded-full"></div>
                                      ) : (
                                        <CopyIcon size="md" />
                                      )}
                                    </button>
                                    
                                    {/* Delete Version Button */}
                                    <button
                                      onClick={() => handleDeleteWebsite(website.id, containerId)}
                                      className="p-2 text-slate-500 hover:bg-red-500 hover:text-white rounded-xl transition-all"
                                      title="Delete this version"
                                      onMouseEnter={() => setHoveredAction(`${website.id}-delete`)}
                                      onMouseLeave={() => setHoveredAction(null)}
                                    >
                                      <TrashIcon size="md" />
                                    </button>
                                  </div>
                                </div>

                                {/* Mobile Layout: Two rows */}
                                <div className="block md:hidden space-y-3">
                                  {/* First Row: Status Badge and Website Info */}
                                  <div className="flex items-start space-x-3">
                                    {/* Status Badge + Latest badge */}
                                    <div className="inline-flex flex-shrink-0 flex-col items-start gap-2">
                                      <motion.div
                                        className="inline-flex"
                                        animate={recentlyPublished === website.id ? {
                                          scale: [1, 1.1, 1],
                                        } : {}}
                                        transition={{ duration: 0.4 }}
                                      >
                                        <StatusBadge variant={website.is_published ? "success" : "warning"} size="sm">
                                          {website.is_published ? "Published" : "Draft"}
                                        </StatusBadge>
                                      </motion.div>
                                      {website.id === latestVersionId && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                          Latest
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Website Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2">
                                        {editingVersion === website.id ? (
                                          <>
                                            <input
                                              type="text"
                                              value={editingName}
                                              onChange={(e) => setEditingName(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  handleSaveVersionName(website.id, containerId);
                                                } else if (e.key === 'Escape') {
                                                  handleCancelEditingVersion();
                                                }
                                              }}
                                              className="text-sm font-medium text-slate-900 dark:text-white border border-slate-300 dark:border-dark-600 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm flex-1"
                                              placeholder="Untitled"
                                              autoFocus
                                            />
                                            <button
                                              onClick={() => handleSaveVersionName(website.id, containerId)}
                                              className="p-1 text-green-600 hover:text-green-800 transition-colors flex-shrink-0"
                                              title="Save"
                                            >
                                              <CheckIcon size="sm" />
                                            </button>
                                            <button
                                              onClick={handleCancelEditingVersion}
                                              className="p-1 text-red-600 hover:text-red-800 transition-colors flex-shrink-0"
                                              title="Cancel"
                                            >
                                              <XIcon size="sm" />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                                              {website.version_name || "Untitled"}
                                            </p>
                                            <button
                                              onClick={() => handleStartEditingVersion(website)}
                                              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors flex-shrink-0"
                                              title="Edit version name"
                                            >
                                              <PencilIcon size="sm" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      <p className="text-xs text-slate-500 dark:text-dark-400 mt-0.5">
                                        Last edited: {getDisplayDateForWebsite(website)}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Second Row: Version Actions */}
                                  <div className="flex items-center justify-center space-x-2">
                                    {/* Go To Published Website */}
                                    {website.is_published && website.domain && (
                                      <a
                                        href={`${APP_URL_WWW}/${username}/${website.domain}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 text-slate-500 hover:bg-indigo-500 hover:text-white rounded-xl transition-all group"
                                        title="Open Published Website"
                                      >
                                        <ExternalLinkIcon size="md" />
                                      </a>
                                    )}

                                    {/* Publish/Unpublish Button */}
                                    {website.is_published ? (
                                      <button
                                        onClick={() => handleUnpublishWebsite(website.id, containerId)}
                                        className="p-2 text-slate-500 hover:bg-yellow-500 hover:text-white rounded-xl transition-all"
                                        title="Unpublish this version"
                                      >
                                        <DraftIcon size="md" />
                                      </button>
                                    ) : (
                                      <motion.button
                                        onClick={() => handlePublishWebsite(website.id, containerId)}
                                        className="p-2 text-slate-500 hover:bg-green-500 hover:text-white rounded-xl transition-all"
                                        title="Publish this version"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        <PublishIcon size="md" />
                                      </motion.button>
                                    )}
                                    
                                    {/* Edit Button / AI Building indicator */}
                                    {website.ai_generation_status === "queued" || website.ai_generation_status === "running" ? (
                                      <span
                                        className="p-2 rounded-xl text-amber-500 dark:text-amber-400 cursor-default"
                                        title="AI is building this version…"
                                      >
                                        <div className="w-5 h-5 animate-spin border-2 border-amber-500 dark:border-amber-400 border-t-transparent rounded-full" />
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handlePrepareForEdit(website, containerId)}
                                        disabled={preparingForEdit === website.id}
                                        className={`p-2 rounded-xl transition-all ${
                                          preparingForEdit === website.id
                                            ? "text-slate-400 cursor-not-allowed"
                                            : "text-slate-500 hover:bg-blue-500 hover:text-white"
                                        }`}
                                        title={preparingForEdit === website.id ? "Preparing…" : "Edit this version"}
                                      >
                                        {preparingForEdit === website.id ? (
                                          <div className="w-5 h-5 animate-spin border-2 text-slate-400 border-t-transparent rounded-full"></div>
                                        ) : (
                                          <EditIcon size="md" />
                                        )}
                                      </button>
                                    )}
                                    
                                    {/* Copy Button */}
                                    <button
                                      onClick={() => handleCopyWebsite(website, containerId)}
                                      disabled={copyingWebsite === website.id}
                                      className={`p-2 rounded-xl transition-all ${
                                        copyingWebsite === website.id
                                          ? "text-slate-400 cursor-not-allowed"
                                          : "text-slate-500 hover:bg-purple-500 hover:text-white"
                                      }`}
                                      title={copyingWebsite === website.id ? "Copying…" : "Copy this version"}
                                    >
                                      {copyingWebsite === website.id ? (
                                        <div className="w-5 h-5 animate-spin border-2 text-slate-400 border-t-transparent rounded-full"></div>
                                      ) : (
                                        <CopyIcon size="md" />
                                      )}
                                    </button>
                                    
                                    {/* Delete Version Button */}
                                    <button
                                      onClick={() => handleDeleteWebsite(website.id, containerId)}
                                      className="p-2 text-slate-500 hover:bg-red-500 hover:text-white rounded-xl transition-all"
                                      title="Delete this version"
                                    >
                                      <TrashIcon size="md" />
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                          
                          {/* Show/Hide Older Versions Button */}
                          {hasMoreVersions && (
                            <div className={`pt-3 border-t ${borders.glass}`}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleShowAllVersions(containerId);
                                }}
                                className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-600 dark:text-dark-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-dark-800/50 rounded-xl transition-all"
                              >
                                {showingAllVersions ? (
                                  <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                                    </svg>
                                    Show fewer versions
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                    Show {allVersions.length - 3} older version{allVersions.length - 3 !== 1 ? 's' : ''}
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      
      {/* Settings Modal */}
      <WebsiteSettingsModal
        isOpen={!!settingsContainer}
        onClose={closeContainerSettings}
        container={settingsContainer}
        versions={settingsContainer ? versionsByContainer[settingsContainer.id]?.versions || [] : []}
        onSave={handleSettingsSaved}
        onRequestDelete={(containerId) => {
          closeContainerSettings();
          handleDeleteContainer(containerId);
        }}
        username={username}
        timezone={preferences.timezone}
      />

    </>
  );
};

export default UserWebsites; 